import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { CompareStore, CompareUpdateEvent } from '@theme/compare-store';

/**
 * A custom element that manages a product compare drawer.
 * Shows a side-by-side comparison table of up to 4 products.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CompareDrawerComponent extends DialogComponent {
  /** Cache fetched product details to avoid re-fetching */
  #productCache = new Map();

  /** Tracks items recently added to cart (show checkmark) */
  #addedHandles = new Set();

  /** Watches for morph resetting the badge */
  #badgeObserver = new MutationObserver(() => {
    this.#badgeObserver.disconnect();
    this.#updateBadge();
    this.#watchBadge();
  });

  /** Watches for morph resetting the drawer content */
  #contentObserver = new MutationObserver(() => {
    this.#contentObserver.disconnect();
    if (this.refs.dialog?.open) {
      this.#render();
    }
    this.#watchContent();
  });

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CompareUpdateEvent.eventName, this.#handleUpdate);
    this.#updateBadge();
    this.#watchBadge();
    this.#watchContent();

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CompareUpdateEvent.eventName, this.#handleUpdate);
    this.#badgeObserver.disconnect();
    this.#contentObserver.disconnect();
  }

  #watchBadge() {
    const badge = document.querySelector('.compare-drawer__badge');
    if (badge) {
      this.#badgeObserver.observe(badge, { attributes: true, childList: true, characterData: true, subtree: true });
    }
  }

  #watchContent() {
    const content = this.refs.dialog?.querySelector('.compare-drawer__content');
    if (content) {
      this.#contentObserver.observe(content, { childList: true, subtree: true, attributes: true });
    }
  }

  async open() {
    await this.#fetchAllDetails();
    this.#render();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Remove a product from comparison.
   * @param {PointerEvent} event
   */
  removeItem(event) {
    event.stopPropagation();
    const button = event.target.closest('[data-handle]');
    if (button) {
      CompareStore.remove(button.dataset.handle);
    }
  }

  /**
   * Clear all compared products.
   * @param {PointerEvent} event
   */
  clearAll(event) {
    event.preventDefault();
    event.stopPropagation();
    CompareStore.clearAll();
  }

  /**
   * Add a single product to cart.
   * @param {PointerEvent} event
   */
  async addToCart(event) {
    event.stopPropagation();
    const button = event.target.closest('[data-variant-id]');
    if (!button) return;

    const variantId = button.dataset.variantId;
    const handle = button.dataset.handle;
    button.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const cart = await response.json();

      this.#addedHandles.add(handle);
      this.dispatchEvent(new CartAddEvent(cart, variantId, { source: 'compare', itemCount: 1 }));

      this.#contentObserver.disconnect();
      this.#render();
      this.#watchContent();

      setTimeout(() => {
        this.#addedHandles.delete(handle);
        if (this.refs.dialog?.open) {
          this.#contentObserver.disconnect();
          this.#render();
          this.#watchContent();
        }
      }, 2000);
    } catch (e) {
      console.error('Compare add to cart failed:', e);
      button.disabled = false;
    }
  }

  /**
   * Add all compared products to cart.
   * @param {PointerEvent} event
   */
  async addAllToCart(event) {
    event.stopPropagation();
    const button = event.target.closest('.compare-drawer__add-all');
    if (!button) return;

    const items = CompareStore.items
      .filter((item) => item.variantId)
      .map((item) => ({ id: Number(item.variantId), quantity: 1 }));

    if (items.length === 0) return;

    button.disabled = true;
    button.textContent = 'Wird hinzugefügt…';

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const cart = await response.json();
      this.dispatchEvent(new CartAddEvent(cart, 'compare-all', { source: 'compare', itemCount: items.length }));

      button.textContent = 'Hinzugefügt ✓';
      setTimeout(() => {
        button.textContent = 'Alle in den Warenkorb';
        button.disabled = false;
      }, 1500);
    } catch (e) {
      console.error('Compare add all to cart failed:', e);
      button.textContent = 'Alle in den Warenkorb';
      button.disabled = false;
    }
  }

  #handleUpdate = () => {
    this.#updateBadge();
    this.#contentObserver.disconnect();
    if (this.refs.dialog?.open) {
      this.#render();
    }
    this.#watchContent();
  };

  #updateBadge() {
    this.#badgeObserver.disconnect();
    const count = CompareStore.count;

    document.querySelectorAll('.compare-drawer__badge').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });

    this.#watchBadge();
  }

  /**
   * Fetch product JSON for all compare items (cached).
   */
  async #fetchAllDetails() {
    const items = CompareStore.items;
    const promises = items
      .filter((item) => !this.#productCache.has(item.handle))
      .map((item) => this.#fetchProductDetails(item.handle));
    await Promise.allSettled(promises);
  }

  /**
   * Fetch and cache full product details from Shopify JSON API.
   * @param {string} handle
   */
  async #fetchProductDetails(handle) {
    try {
      const response = await fetch(`/products/${handle}.json`);
      if (!response.ok) return;
      const data = await response.json();
      console.log(`[Compare] Product "${handle}" API data:`, { available: data.product?.available, variants: data.product?.variants?.map(v => ({ id: v.id, available: v.available, inventory_policy: v.inventory_policy, inventory_quantity: v.inventory_quantity })) });
      this.#productCache.set(handle, data.product);
    } catch {
      // Silently fail — render will use stored basic data
    }
  }

  /**
   * Get merged product data: localStorage basics + API details.
   * @param {object} storedItem
   * @returns {object}
   */
  #getFullProduct(storedItem) {
    const details = this.#productCache.get(storedItem.handle);
    return {
      ...storedItem,
      description: details?.body_html ? this.#stripHtml(details.body_html).slice(0, 150) : '',
      vendor: details?.vendor || '',
      productType: details?.product_type || '',
      available: details
        ? (details.available !== undefined ? details.available : details.variants?.some((v) => v.available !== false))
        : true,
      compareAtPrice: details?.variants?.[0]?.compare_at_price
        ? this.#formatMoney(details.variants[0].compare_at_price)
        : '',
      options: details?.options?.filter((o) => o.name !== 'Title') || [],
    };
  }

  #stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  }

  #formatMoney(cents) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }

  #render() {
    const items = CompareStore.items;
    const container = this.refs.dialog?.querySelector('.compare-drawer__table-wrap');
    const emptyState = this.refs.dialog?.querySelector('.compare-drawer__empty');
    const headerTitle = this.refs.dialog?.querySelector('.compare-drawer__heading');
    const footer = this.refs.dialog?.querySelector('.compare-drawer__footer');

    if (!container) return;

    if (items.length === 0) {
      container.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      if (headerTitle) headerTitle.textContent = 'Produktvergleich';
      if (footer) footer.hidden = true;
      return;
    }

    if (emptyState) emptyState.hidden = true;
    if (headerTitle) headerTitle.textContent = `Produktvergleich (${items.length})`;
    if (footer) footer.hidden = false;

    const products = items.map((item) => this.#getFullProduct(item));
    const colCount = products.length;

    const rows = [
      // Product images + remove buttons
      {
        label: '',
        cells: products.map(
          (p) => `
          <div class="compare-drawer__cell-product">
            <button class="compare-drawer__remove button-unstyled" on:click="/removeItem" data-handle="${p.handle}" aria-label="Entfernen" type="button">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
                <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            ${p.image && !p.image.includes('Liquid error')
              ? `<a href="${p.url}"><img src="${p.image}" alt="${p.title}" class="compare-drawer__product-img" width="120" height="120" loading="lazy"/></a>`
              : '<div class="compare-drawer__product-img-placeholder"></div>'}
          </div>`
        ),
      },
      // Title
      {
        label: 'Produkt',
        cells: products.map((p) => `<a href="${p.url}" class="compare-drawer__product-title">${p.title}</a>`),
      },
      // Price
      {
        label: 'Preis',
        cells: products.map((p) => {
          if (p.compareAtPrice) {
            return `<span class="compare-drawer__price-sale">${p.price}</span> <s class="compare-drawer__price-compare">${p.compareAtPrice}</s>`;
          }
          return `<span class="compare-drawer__price">${p.price}</span>`;
        }),
      },
      // Availability
      {
        label: 'Verfügbar',
        cells: products.map(
          (p) =>
            `<span class="compare-drawer__availability compare-drawer__availability--${p.available ? 'yes' : 'no'}">${p.available ? 'Auf Lager' : 'Ausverkauft'}</span>`
        ),
      },
      // Vendor
      {
        label: 'Hersteller',
        cells: products.map((p) => p.vendor || '–'),
      },
      // Product Type
      {
        label: 'Typ',
        cells: products.map((p) => p.productType || '–'),
      },
      // Description
      {
        label: 'Beschreibung',
        cells: products.map((p) => (p.description ? `<span class="compare-drawer__description">${p.description}…</span>` : '–')),
      },
      // Options / Variants
      ...this.#getOptionRows(products),
      // Add to cart
      {
        label: '',
        cells: products.map((p) => {
          if (this.#addedHandles.has(p.handle)) {
            return `<button class="compare-drawer__cart-btn button button--primary" disabled type="button">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Hinzugefügt
            </button>`;
          }
          if (!p.available) {
            return `<button class="compare-drawer__cart-btn button button--secondary" disabled type="button">Ausverkauft</button>`;
          }
          if (!p.variantId) {
            return `<a href="${p.url}" class="compare-drawer__cart-btn button button--secondary">Optionen wählen</a>`;
          }
          return `<button class="compare-drawer__cart-btn button button--primary" on:click="/addToCart" data-variant-id="${p.variantId}" data-handle="${p.handle}" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
            </svg>
            In den Warenkorb
          </button>`;
        }),
      },
    ];

    container.innerHTML = `
      <table class="compare-drawer__table" style="--compare-cols: ${colCount}">
        <tbody>
          ${rows
            .map(
              (row) => `
            <tr class="compare-drawer__row${row.label === '' ? ' compare-drawer__row--no-label' : ''}">
              ${row.label ? `<th class="compare-drawer__label">${row.label}</th>` : '<th class="compare-drawer__label"></th>'}
              ${row.cells.map((cell) => `<td class="compare-drawer__cell">${cell}</td>`).join('')}
            </tr>`
            )
            .join('')}
        </tbody>
      </table>
    `;
  }

  /**
   * Build option rows from product variant options.
   * @param {object[]} products
   * @returns {{label: string, cells: string[]}[]}
   */
  #getOptionRows(products) {
    // Collect all unique option names
    const allOptionNames = new Set();
    products.forEach((p) => p.options?.forEach((o) => allOptionNames.add(o.name)));

    return Array.from(allOptionNames).map((name) => ({
      label: name,
      cells: products.map((p) => {
        const option = p.options?.find((o) => o.name === name);
        return option ? option.values.join(', ') : '–';
      }),
    }));
  }
}

if (!customElements.get('compare-drawer-component')) {
  customElements.define('compare-drawer-component', CompareDrawerComponent);
}
